import { type ReactNode } from 'react';
import { GraduationCap, LayoutGrid, Lock, Shield, ShieldCheck, Tv, User, Zap } from 'lucide-react';
import { IconCreditCard } from '@/components/landing/LandingIcons';
import { IconNavReceipt } from '@/components/icons/DashboardIcons';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { QuickActionCard } from '@/components/user/QuickActionCard';
import { ROUTES } from '@/constants/routes';
import { useUserAuth } from '@/context/UserAuthContext';

function MoreSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-bold text-brand-navy">{title}</h2>
      <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-4 sm:gap-3">{children}</div>
    </section>
  );
}

export function UserMorePage() {
  const { user } = useUserAuth();
  const { openPinModal } = useDashboardUi();
  const pinEnabled = user?.pinEnabled ?? false;

  return (
    <div className="space-y-6 pb-8 sm:pb-12">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">More</h1>
        <p className="text-sm text-brand-muted">Bills, account settings, and other services</p>
      </div>

      <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-soft sm:p-5">
        <MoreSection title="Pay bills">
          <QuickActionCard
            title="Electricity"
            to={ROUTES.dashboardElectricity}
            tone="orange"
            variant="compact"
            icon={<Zap className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Cable TV"
            to={ROUTES.dashboardCableTv}
            tone="green"
            variant="compact"
            icon={<Tv className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Exams"
            to={ROUTES.dashboardExams}
            tone="purple"
            variant="compact"
            icon={<GraduationCap className="h-5 w-5" />}
          />
        </MoreSection>

        <MoreSection title="Verification & services">
          <QuickActionCard
            title="NIN"
            to={ROUTES.dashboardVerifyNin}
            tone="green"
            variant="compact"
            icon={<Shield className="h-5 w-5" />}
          />
          <QuickActionCard
            title="BVN"
            to={ROUTES.dashboardVerifyBvn}
            tone="blue"
            variant="compact"
            icon={<IconCreditCard className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Verification"
            to={ROUTES.dashboardServices}
            tone="orange"
            variant="compact"
            icon={<LayoutGrid className="h-5 w-5" />}
          />
        </MoreSection>

        <MoreSection title="Account & security">
          <QuickActionCard
            title="Profile"
            to={ROUTES.dashboardProfile}
            tone="navy"
            variant="compact"
            icon={<User className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Security"
            to={ROUTES.dashboardSecurity}
            tone="navy"
            variant="compact"
            icon={<Lock className="h-5 w-5" />}
          />
          {!pinEnabled ? (
            <QuickActionCard
              title="Set PIN"
              onClick={openPinModal}
              tone="orange"
              variant="compact"
              icon={<ShieldCheck className="h-5 w-5" />}
            />
          ) : (
            <QuickActionCard
              title="Change PIN"
              to={ROUTES.dashboardSecurity}
              tone="green"
              variant="compact"
              icon={<ShieldCheck className="h-5 w-5" />}
            />
          )}
          <QuickActionCard
            title="Transactions"
            to={ROUTES.dashboardTransactions}
            tone="navy"
            variant="compact"
            icon={<IconNavReceipt className="h-5 w-5" />}
          />
        </MoreSection>
      </div>
    </div>
  );
}
